/**
 * Admin user management API (via pro-api BFF).
 */

import { dedupePermissionCatalog } from "../permission-name-canonical";
import { authenticatedProApiFetch } from "./pro-api.client";
import { normalizeUserProfile, type User } from "./users";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";

export interface AdminUserListItem {
  id: string;
  isMocked?: boolean;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
  phone?: string | null;
  countryCode?: string | null;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  profileSetupCompleted?: boolean;
  googleId?: string | null;
  lockedAt?: string | null;
  deletedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  organizations?: Array<{ id: string; name: string }>;
  pendingMembershipOrgs?: Array<{ id: string; name: string; logoUrl?: string | null }>;
  /** Cheap, pre-computed Smart Link sync-status badge signal — null when not linked (or link disabled). */
  smartLinkSyncStatus?: "in_sync" | "out_of_sync" | "unknown" | null;
}

export interface RoleSummary {
  id: string;
  name: string;
  description?: string | null;
  scope?: string;
}

function normalizeRoleSummary(raw: Record<string, unknown>): RoleSummary {
  return {
    id: String(raw.id ?? ""),
    name: String(raw.name ?? ""),
    description:
      raw.description != null ? String(raw.description) : null,
    scope: raw.scope != null ? String(raw.scope) : "global",
  };
}

/** Platform roles assignable via user_roles (excludes organization-scoped roles). */
export async function listGlobalRoles(
  accessToken: string,
): Promise<RoleSummary[]> {
  const roles = await listRoles(accessToken);
  return roles.filter((r) => (r.scope ?? "global") === "global");
}

function normalizeListUser(raw: Record<string, unknown>): AdminUserListItem {
  const profileSetupCompletedAt =
    raw.profileSetupCompletedAt ?? raw.profile_setup_completed_at;
  const orgsRaw = raw.organizations;
  const organizations = Array.isArray(orgsRaw)
    ? orgsRaw
        .map((entry) => {
          const row = entry as Record<string, unknown>;
          const id = row.id != null ? String(row.id) : "";
          const name = row.name != null ? String(row.name) : "";
          if (!id) return null;
          return { id, name: name || "Organization" };
        })
        .filter((o): o is { id: string; name: string } => o != null)
    : undefined;
  return {
    id: String(raw.id ?? ""),
    email: String(raw.email ?? ""),
    firstName: String(raw.firstName ?? raw.first_name ?? ""),
    lastName: String(raw.lastName ?? raw.last_name ?? ""),
    avatarUrl:
      raw.avatarUrl != null
        ? String(raw.avatarUrl)
        : raw.avatar_url != null
          ? String(raw.avatar_url)
          : null,
    phone: raw.phone != null ? String(raw.phone) : null,
    countryCode:
      raw.countryCode != null
        ? String(raw.countryCode)
        : raw.country_code != null
          ? String(raw.country_code)
          : null,
    isEmailVerified: Boolean(raw.isEmailVerified ?? raw.is_email_verified),
    isPhoneVerified: Boolean(raw.isPhoneVerified ?? raw.is_phone_verified),
    profileSetupCompleted: Boolean(
      raw.profileSetupCompleted ??
        raw.profile_setup_completed ??
        (profileSetupCompletedAt != null &&
          String(profileSetupCompletedAt) !== ""),
    ),
    googleId:
      raw.googleId != null
        ? String(raw.googleId)
        : raw.google_id != null
          ? String(raw.google_id)
          : null,
    lockedAt:
      raw.lockedAt != null
        ? String(raw.lockedAt)
        : raw.locked_at != null
          ? String(raw.locked_at)
          : null,
    deletedAt:
      raw.deletedAt != null
        ? String(raw.deletedAt)
        : raw.deleted_at != null
          ? String(raw.deleted_at)
          : null,
    createdAt:
      raw.createdAt != null
        ? String(raw.createdAt)
        : raw.created_at != null
          ? String(raw.created_at)
          : undefined,
    updatedAt:
      raw.updatedAt != null
        ? String(raw.updatedAt)
        : raw.updated_at != null
          ? String(raw.updated_at)
          : undefined,
    ...(organizations ? { organizations } : {}),
    pendingMembershipOrgs: Array.isArray(raw.pendingMembershipOrgs)
      ? (raw.pendingMembershipOrgs as unknown[]).map((entry) => {
          const e = entry as Record<string, unknown>;
          return {
            id: String(e.id ?? ""),
            name: String(e.name ?? ""),
            logoUrl: e.logoUrl != null ? String(e.logoUrl) : e.logo_url != null ? String(e.logo_url) : null,
          };
        }).filter((e) => e.id)
      : [],
    isMocked: Boolean(raw.isMocked ?? raw.is_mocked ?? false),
    smartLinkSyncStatus:
      (raw.smartLinkSyncStatus as AdminUserListItem["smartLinkSyncStatus"]) ?? null,
  };
}

function buildUsersListQuery(params: ListParams): string {
  const qs = new URLSearchParams();
  qs.set("page", String(params.page));
  qs.set("limit", String(params.limit));
  if (params.search) qs.set("search", params.search);
  if (params.sort_by) qs.set("sort_by", params.sort_by);
  if (params.order) qs.set("order", params.order);
  if (params.filters && Object.keys(params.filters).length > 0) {
    qs.set("filters", JSON.stringify(params.filters));
  }
  return qs.toString();
}

/** Paginated admin user list (server-side search, filter, sort). */
export async function fetchUsersList(
  accessToken: string,
  params: ListParams,
): Promise<ListResult<AdminUserListItem>> {
  const query = buildUsersListQuery(params);
  const data = await authenticatedProApiFetch<{
    users?: Record<string, unknown>[];
    total?: number;
  }>(`/users?${query}`, accessToken, { method: "GET" });
  const rows = data.users ?? [];
  return {
    data: rows.map(normalizeListUser),
    total: Number(data.total ?? rows.length),
  };
}

/** @deprecated Prefer fetchUsersList for tables; loads all users in one request. */
export async function listUsers(accessToken: string): Promise<AdminUserListItem[]> {
  const result = await fetchUsersList(accessToken, {
    page: 1,
    limit: 100,
  });
  return result.data;
}

export async function getAdminUserById(
  accessToken: string,
  userId: string,
): Promise<Record<string, unknown>> {
  return authenticatedProApiFetch<Record<string, unknown>>(`/users/${userId}`, accessToken, {
    method: "GET",
  });
}

export async function getAdminUserProfile(
  accessToken: string,
  userId: string,
): Promise<User> {
  const raw = await getAdminUserById(accessToken, userId);
  return normalizeUserProfile(raw);
}

export async function listRoles(accessToken: string): Promise<RoleSummary[]> {
  const data = await authenticatedProApiFetch<
    RoleSummary[] | { roles?: Record<string, unknown>[] }
  >("/roles", accessToken, { method: "GET" });
  const rows = Array.isArray(data) ? data : (data.roles ?? []);
  return rows.map((row) =>
    normalizeRoleSummary(row as Record<string, unknown>),
  );
}

export interface PermissionSummary {
  id: string;
  name: string;
  description?: string | null;
  resource?: string | null;
  action?: string | null;
}

function normalizePermissionSummary(
  raw: Record<string, unknown>,
): PermissionSummary {
  return {
    id: String(raw.id ?? ""),
    name: String(raw.name ?? ""),
    description:
      raw.description != null ? String(raw.description) : null,
    resource: raw.resource != null ? String(raw.resource) : null,
    action: raw.action != null ? String(raw.action) : null,
  };
}

const LION_PRO_PLATFORM_PERMISSION_NAMES = new Set([
  "user:read",
  "user:undelete",
  "user:delete",
  "user:hard-delete",
  "user:update",
  "user:masquerade",
  "role:read",
  "role:create",
  "role:update",
  "role:delete",
  "permission:read",
  "permission:create",
  "permission:update",
  "permission:delete",
  "organization:list",
  "organization:create",
  "organization:update",
  "organization:delete",
]);

export async function listPermissions(
  accessToken: string,
): Promise<PermissionSummary[]> {
  const data = await authenticatedProApiFetch<
    PermissionSummary[] | Record<string, unknown>[]
  >("/roles/permissions", accessToken, { method: "GET" });
  const rows = Array.isArray(data) ? data : [];
  return rows.map((row) =>
    normalizePermissionSummary(row as Record<string, unknown>),
  );
}

export async function listLionProPlatformPermissions(
  accessToken: string,
): Promise<PermissionSummary[]> {
  const all = await listPermissions(accessToken);
  return dedupePermissionCatalog(
    all.filter((p) => LION_PRO_PLATFORM_PERMISSION_NAMES.has(p.name)),
  ).sort((a, b) => a.name.localeCompare(b.name));
}

/** Legacy Smart ecosystem permissions (excludes Lion Pro platform and org-scoped). */
export async function listLionSmartPermissions(
  accessToken: string,
): Promise<PermissionSummary[]> {
  const all = await listPermissions(accessToken);
  return dedupePermissionCatalog(
    all.filter(
      (p) =>
        !LION_PRO_PLATFORM_PERMISSION_NAMES.has(p.name) &&
        !p.name.startsWith("org:"),
    ),
  ).sort((a, b) => a.name.localeCompare(b.name));
}

export async function updateUserDirectPermissions(
  accessToken: string,
  userId: string,
  permissionIds: string[],
): Promise<void> {
  await authenticatedProApiFetch(
    `/roles/users/${userId}/permissions`,
    accessToken,
    {
      method: "POST",
      body: JSON.stringify({ permissionIds }),
    },
  );
}

export async function updateUserRoles(
  accessToken: string,
  userId: string,
  roleIds: string[],
): Promise<void> {
  await authenticatedProApiFetch(`/roles/users/${userId}/roles`, accessToken, {
    method: "POST",
    body: JSON.stringify({ roleIds }),
  });
}

export async function patchAdminUser(
  accessToken: string,
  userId: string,
  body: { firstName?: string; lastName?: string },
): Promise<void> {
  await authenticatedProApiFetch(`/users/${userId}`, accessToken, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function disableUser(
  accessToken: string,
  userId: string,
): Promise<{ message: string }> {
  return authenticatedProApiFetch<{ message: string }>(
    `/users/${userId}/soft-delete`,
    accessToken,
    { method: "POST" },
  );
}

export async function deleteUserPermanently(
  accessToken: string,
  userId: string,
): Promise<{ message: string; id?: string }> {
  return authenticatedProApiFetch<{ message: string; id?: string }>(
    `/users/${userId}/hard-delete`,
    accessToken,
    { method: "POST" },
  );
}

export async function restoreUser(
  accessToken: string,
  userId: string,
): Promise<{ message: string; resetToken?: string }> {
  return authenticatedProApiFetch<{ message: string; resetToken?: string }>(
    `/users/${userId}/undelete`,
    accessToken,
    { method: "POST" },
  );
}

export async function disableMyAccount(
  accessToken: string,
): Promise<{ message: string }> {
  return authenticatedProApiFetch<{ message: string }>(
    "/users/me/disable",
    accessToken,
    { method: "POST" },
  );
}

export async function deleteMyAccount(
  accessToken: string,
): Promise<{ message: string; id?: string }> {
  return authenticatedProApiFetch<{ message: string; id?: string }>(
    "/users/me/delete",
    accessToken,
    { method: "POST" },
  );
}
